import type {
  SelectChangeEvent} from '@mui/material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getI18nSupportedLanguageList, I18nLanguageNames, I18nLanguage } from '../../../i18n/types/i18nLanguage.ts';
import { selectedLanguage } from '@/features/language/signals.ts';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs, I18nCoreNs } from '@/i18n/dictionaries/interfaces.ts';
import { Box } from '@mui/system';

type LanguageSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const enum BrowserLanguages {
  English = 'en',
  Norwegian = 'no',
  German = 'de',
  EnglishUs = 'en-US',
  EnglishUk = 'en-GB'
}

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  '& .container': {
    padding: theme.spacing(2)
  },
  '& .formControl': {
    minWidth: '12em'
  },
  '& .tooltip': {
    fontSize: '0.75rem'
  }
}));

const LanguageSettingsDialog = (props: LanguageSettingsDialogProps) => {
  const { open, onClose } = props;
  const { i18n } = useTranslation();
  const [translateCommon] = useTranslation([I18nNamespace.Common]);
  const [translateCore] = useTranslation([I18nNamespace.Core]);
  const [currentLanguage, setCurrentLanguage] = useState(selectedLanguage.value);

  // Update local state when the signal changes
  useEffect(() => {
    setCurrentLanguage(selectedLanguage.value);
  }, [selectedLanguage.value]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    console.log("language set to " + event.target.value);
    setCurrentLanguage(event.target.value as I18nLanguage);
  };

  const onLocalClose = () => {
    onClose();
  };

  const onSave = () => {
    i18n.changeLanguage(currentLanguage);
    // The signal and localStorage are updated automatically via the i18n.on('languageChanged') handler
    onLocalClose();
  };

  return (
    (<Dialog open={open} onClose={onLocalClose}>
      <StyledDialogContent>
        <Grid container direction="column" className="container" spacing={2}>
          <Grid item>
            <Typography variant="h6" gutterBottom>
              {translateCommon(nameof.full<I18nCommonNs>((n) => n.languageSettings.selectLanguage))}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" gutterBottom>
              Sklls {translateCommon(nameof.full<I18nCommonNs>((n) => n.languageSettings.explanationText))}
            </Typography>
          </Grid>
          <Grid item>
            <FormControl className="formControl">
              <InputLabel id="language-select-label">Language</InputLabel>
              <Select
                variant="outlined"
                labelId="language-select-label"
                label="Language"
                value={currentLanguage}
                onChange={handleLanguageChange}
                sx={{ 
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                    paddingLeft: 2
                  }
                }}>
                <MenuItem key={BrowserLanguages.English} data-cy="English" value={BrowserLanguages.English}>
                  {translateCore(nameof.full<I18nCoreNs>((l) => l.languagePicker.english))}
                </MenuItem>
                <MenuItem key={BrowserLanguages.Norwegian} data-cy="Norwegian" value={BrowserLanguages.Norwegian}>
                  {translateCore(nameof.full<I18nCoreNs>((l) => l.languagePicker.norwegian))}
                </MenuItem>
                <MenuItem key={BrowserLanguages.German} data-cy="German" value={BrowserLanguages.German}>
                  {translateCore(nameof.full<I18nCoreNs>((l) => l.languagePicker.german))}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onLocalClose}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
        </Button>
        <Button variant="contained" onClick={onSave}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.save))}
        </Button>
      </DialogActions>
    </Dialog>)
  );
};

export default LanguageSettingsDialog;
